import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import { Eye, ChevronLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUserBookings } from '../../Services/Users/UserApi';
import GlobalPagination from '../../Components/Common/Globalpagination';

const UserBookings = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const userId = location.state?.userId;
    const userName = location.state?.userName;

    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [limit, setLimit] = useState(25);

    const fetchUserBookings = useCallback(async (currentPage = 1, currentLimit = limit) => {
        if (!userId) {
            setError("No user ID provided.");
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const response = await getUserBookings(userId, currentPage, currentLimit);
            if (response && response.success) {
                setBookings(response.data || []);
                setTotalItems(response.totalItems || 0);
                setTotalPages(response.totalPages || 1);
                setError(null);
            } else {
                setBookings([]);
                setError("No bookings found for this user.");
            }
        } catch (err) {
            console.error(err);
            setError("Failed to fetch user bookings.");
            setBookings([]);
        } finally {
            setLoading(false);
        }
    }, [userId, limit]);

    useEffect(() => {
        fetchUserBookings(page);
    }, [fetchUserBookings, page]);

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleViewDetails = (bookingId) => {
        navigate(`/bookings/view-details`, { state: { booking: bookingId } });
    };

    const formatBookingStatus = (status) => {
        if (!status) return 'N/A';
        const map = {
            'pending': 'Pending',
            'confirmed': 'Confirmed',
            'cancelled': 'Cancelled',
            'rejected': 'Rejected',
            'completed': 'Completed',
            'awaiting_host_confirmation': 'Awaiting Host Confirmation',
            'date_change_requested': 'Date Change Requested',
            'date_change_rejected': 'Date Change Rejected',
            'date_change_approved': 'Date Change Approved',
            'user_cancelled_date_change_request': 'User Cancelled Date Change Request',
            'awaiting_admin_confirmation': 'Awaiting Admin Confirmation'
        };
        return map[status.toLowerCase()] || status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'confirmed':
                return { bgcolor: '#E3F2FD', color: '#1976D2' };
            case 'completed':
            case 'success':
                return { bgcolor: '#E8F5E8', color: '#2E7D32' };
            case 'pending':
                return { bgcolor: '#FFF8E1', color: '#F57C00' };
            case 'awaiting_host_confirmation':
                return { bgcolor: '#FFF4E5', color: '#ED6C02' };
            case 'awaiting_admin_confirmation':
                return { bgcolor: '#F3E5F5', color: '#9C27B0' };
            case 'date_change_requested':
                return { bgcolor: '#E1F5FE', color: '#0288D1' };
            case 'date_change_approved':
                return { bgcolor: '#E0F2F1', color: '#00897B' };
            case 'date_change_rejected':
                return { bgcolor: '#FCE4EC', color: '#D81B60' };
            case 'user_cancelled_date_change_request':
                return { bgcolor: '#ECEFF1', color: '#546E7A' };
            case 'failed':
            case 'cancelled':
                return { bgcolor: '#FFEBEE', color: '#C62828' };
            case 'rejected':
                return { bgcolor: '#FFEBEE', color: '#B71C1C' };
            default:
                return { bgcolor: '#F5F5F5', color: '#666' };
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
            {/* Header */}
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 4 }}
            >
                <Stack direction="row" alignItems="center" spacing={2}>
                    <IconButton onClick={() => navigate(-1)} sx={{ color: '#222' }}>
                        <ChevronLeft size={24} />
                    </IconButton>
                    <Box>
                        <Typography
                            sx={{
                                color: '#222',
                                fontWeight: 500,
                                fontSize: { xs: '1.5rem', md: '1.8rem' },
                            }}
                        >
                            Bookings for {userName || 'User'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            User ID: {userId}
                        </Typography>
                    </Box>
                </Stack>

                <Button
                    variant="outlined"
                    onClick={() => navigate('/user')}
                    sx={{ borderRadius: '8px', textTransform: 'none' }}
                >
                    Back to Users
                </Button>
            </Stack>

            {/* Error */}
            {error && <Alert severity="info" sx={{ mb: 3 }}>{error}</Alert>}

            {/* Table */}
            <Paper
                sx={{
                    borderRadius: "8px",
                    border: "1px solid #E8E8E8",
                    overflow: "hidden",
                    boxShadow: 'none'
                }}
                elevation={0}
            >
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap', textAlign: 'center' }}>Booking ID</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Retreat Title</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Amount Paid</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Booking Status</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Payment Status</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Created Date</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                                        <CircularProgress sx={{ color: '#FFBD65' }} />
                                    </TableCell>
                                </TableRow>
                            ) : bookings.length > 0 ? (
                                bookings.map((booking) => (
                                    <TableRow key={booking.id} sx={{
                                        '& td': { borderBottom: '1px solid #F0F0F0' },
                                        '&:last-child td': { borderBottom: 0 }
                                    }}>
                                        <TableCell sx={{ color: '#444', py: 2.5, fontWeight: 500, textAlign: 'center' }}>#{booking.id}</TableCell>

                                        <TableCell sx={{ py: 2.5, maxWidth: 300 }}>
                                            <Typography
                                                sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    fontWeight: 500,
                                                    color: '#333'
                                                }}
                                                title={booking.retreat?.title}
                                            >
                                                {booking.retreat?.title || 'N/A'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ py: 2.5 }}>
                                            <Typography sx={{ fontWeight: 500, color: '#333' }}>
                                                {booking.amountPaid || '0.00'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ py: 2.5 }}>
                                            <Box
                                                sx={{
                                                    display: 'inline-block',
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: '4px',
                                                    bgcolor: getStatusColor(booking.bookingStatus).bgcolor,
                                                    color: getStatusColor(booking.bookingStatus).color,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 500
                                                }}
                                            >
                                                {formatBookingStatus(booking.bookingStatus)}
                                            </Box>
                                        </TableCell>

                                        <TableCell sx={{ py: 2.5 }}>
                                            <Box
                                                sx={{
                                                    display: 'inline-block',
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: '4px',
                                                    bgcolor: getStatusColor(booking.paymentStatus).bgcolor,
                                                    color: getStatusColor(booking.paymentStatus).color,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 500
                                                }}
                                            >
                                                {booking.paymentStatus || 'N/A'}
                                            </Box>
                                        </TableCell>

                                        <TableCell sx={{ py: 2.5 }}>
                                            <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>
                                                {new Date(booking.createdAt).toLocaleDateString()}
                                            </Typography>
                                        </TableCell>

                                        <TableCell align="right" sx={{ py: 2.5 }}>
                                            <Tooltip title="View Details">
                                                <IconButton
                                                    size="small"
                                                    sx={{ color: '#444' }}
                                                    onClick={() => handleViewDetails(booking.id)}
                                                >
                                                    <Eye size={18} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                        No bookings found for this user.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Pagination */}
            <GlobalPagination
                page={page}
                pageSize={limit}
                totalRecords={totalItems}
                totalPages={totalPages}
                onPageChange={(newPage) => {
                    setPage(newPage);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onPageSizeChange={(newSize) => {
                    setLimit(newSize);
                    setPage(1);
                }}
                loading={loading}
            />
        </Box>
    );
};

export default UserBookings;
